import { ref, computed, watch } from 'vue'
import { DEPT_TO_SEGMENT, SUBSEGMENT_TO_REPORT, OVERVIEW_SUBSEGMENT } from '../data/workflowMapping'
import { parseOverviewTags } from '../utils/parseOverviewTags'
import { useActiveReport } from './useActiveReport'
import { useTimeline } from './useTimeline'

const workflowCache = ref({})     // keyed by "date::segment" → array of report rows
const availableDates = ref([])    // date strings with data
const datesWithAnalysis = ref([]) // date strings that have at least one isFinish record
const isLoading = ref(false)
const loadError = ref(null)
const _pendingFetches = new Set()  // track concurrent fetches

async function fetchAvailableDates(segment) {
  try {
    const params = segment ? `?segment=${encodeURIComponent(segment)}` : ''
    const resp = await fetch(`/api/workflow/dates${params}`)
    const data = await resp.json()
    availableDates.value = data.dates || []
    datesWithAnalysis.value = data.datesWithAnalysis || []
  } catch (err) {
    console.error('[useWorkflowData] fetchAvailableDates failed:', err)
  }
}

async function fetchReports(date, segment) {
  if (!date || !segment) return

  const key = `${date}::${segment}`
  if (workflowCache.value[key]) return // cache hit

  _pendingFetches.add(key)
  isLoading.value = true
  loadError.value = null

  try {
    const resp = await fetch(
      `/api/workflow/reports?date=${encodeURIComponent(date)}&segment=${encodeURIComponent(segment)}`
    )
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const data = await resp.json()
    workflowCache.value[key] = data.reports || []
  } catch (err) {
    loadError.value = err.message
    console.error('[useWorkflowData] fetchReports failed:', err)
  } finally {
    _pendingFetches.delete(key)
    // Only set loading false when ALL pending fetches are done
    if (_pendingFetches.size === 0) {
      isLoading.value = false
    }
  }
}

// Singleton setup — computed and watcher created once
let _initialized = false
let _currentSegment = null
let _currentWorkflowReports = null
let _overviewContent = null
let _parsedOverview = null
let _hasWorkflowData = null

function ensureInitialized() {
  if (_initialized) return
  _initialized = true

  const { currentDept } = useActiveReport()
  const { selectedDate } = useTimeline()

  _currentSegment = computed(() => {
    return DEPT_TO_SEGMENT[currentDept.value?.id] || null
  })

  _currentWorkflowReports = computed(() => {
    if (!selectedDate.value || !_currentSegment.value) return null
    const key = `${selectedDate.value}::${_currentSegment.value}`
    return workflowCache.value[key] || null
  })

  _overviewContent = computed(() => {
    if (!_currentWorkflowReports.value) return null
    const row = _currentWorkflowReports.value.find(
      r => r.subSegment === OVERVIEW_SUBSEGMENT
    )
    if (!row) return null
    return row.mdFile || null
  })

  _parsedOverview = computed(() => parseOverviewTags(_overviewContent.value))

  _hasWorkflowData = computed(() => {
    return _currentWorkflowReports.value && _currentWorkflowReports.value.length > 0
  })

  // Auto-fetch when date or department changes
  watch(
    [selectedDate, _currentSegment],
    ([date, segment]) => {
      if (date && segment) {
        fetchReports(date, segment)
      }
    },
    { immediate: true }
  )
}

function getReportContent(reportId) {
  ensureInitialized()
  if (!_currentWorkflowReports.value) return null

  const row = _currentWorkflowReports.value.find(r => {
    return SUBSEGMENT_TO_REPORT[r.subSegment] === reportId
  })
  if (!row) return null

  return {
    hasData: !!(row.isFinish || row.data || row.mdFile),
    isProcessing: !row.isFinish,
    mdContent: row.data || row.mdFile || '',
    mdFile: row.mdFile || '',
    sourceUrl: row.url,
    chatUrl: row.urlCreated,
    sequence: row.sequence,
    updateDate: row.updateDate,
  }
}

export function useWorkflowData() {
  ensureInitialized()

  return {
    availableDates,
    datesWithAnalysis,
    isLoading,
    loadError,
    currentWorkflowReports: _currentWorkflowReports,
    hasWorkflowData: _hasWorkflowData,
    overviewContent: _overviewContent,
    parsedOverview: _parsedOverview,
    getReportContent,
    fetchAvailableDates,
    fetchReports,
  }
}
